import React, { useCallback, useEffect, useState } from 'react';
import { useFileUpload } from '../hooks/useFileUpload';
import {
  ReferenceMinimal,
  TransformedFile,
} from '../hooks/useReferenceFiles/useReferenceFilesTypes';
import { useOneReferenceUtilities } from '../hooks/useReferenceFiles/useOneReferenceUtilities';
import styles from './FileUploadDialog.module.scss';
import DocumentUrlUploader from './DocumentUrlUploader';
import DragAndDropFileUploader from './DragAndDropFileUploader';
import FileList from './FileList';
import { useDropZoneContext } from './DropZone';
import { Button, createToast, Toast } from 'components/core';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'components/core';
import { Stack } from 'components/layout';
import { UPLOAD_METHODS } from 'util/pendoTrack';

export type SaveDocumentResponse = {
  id: string;
  authenticatedUrl: string;
  uploadedBy: string;
  createdAt: string;
};

type FileUploadDialogProps = {
  reviewId: string;
  reference: ReferenceMinimal;
  isModalOpen?: boolean;
  shouldForceRefresh?: boolean; // this is only added to support old DE1
  saveDocument: (
    referenceId: number,
    file: File,
    documentUrl: string
  ) => Promise<SaveDocumentResponse>;
  submitDocumentUrl: (url: string) => Promise<void>;
  onClose?: (numberOfFullTexts: number) => void;
};

export const FileUploadDialog = ({
  reviewId,
  reference,
  isModalOpen = false,
  shouldForceRefresh = false,
  saveDocument,
  submitDocumentUrl,
  onClose,
}: FileUploadDialogProps) => {
  const documentUrl = reference.document_url;
  const [isOpen, setOpen] = useState(isModalOpen);
  const hasDroppedFiles = useDropZoneContext();

  const [pdfFiles, setPdfFiles] = useState<TransformedFile[]>([]);

  const {
    files: referenceFiles,
    makeFilePrimary: makePdfFilePrimary,
    deleteFile: deletePdfFile,
  } = useOneReferenceUtilities({
    reviewId,
    reference,
    referenceUrl: reference.url,
    shouldForceRefresh,
  });

  const { hasError, uploadingCount, clearError, handleUpload } = useFileUpload({
    reference,
    reviewId,
    saveDocument,
    updateFiles: setPdfFiles,
  });

  const showErrorToast = useCallback((error?: string) => {
    createToast(
      <Toast type="danger">
        {error || 'Something went wrong, please try again later.'}
      </Toast>
    );
  }, []);

  const handleUploadFile = useCallback(
    async (file: File) => {
      const uploadMethod = hasDroppedFiles.isDialogOpenFromDrop
        ? UPLOAD_METHODS.DRAG_AND_DROP_SCREENING_PAGE
        : UPLOAD_METHODS.SINGLE_STUDY_MODAL;

      handleUpload(file, uploadMethod);
    },
    [hasDroppedFiles.isDialogOpenFromDrop, handleUpload]
  );

  const handleOnDismiss = () => {
    setOpen(false);
    hasDroppedFiles.clearFiles();
    hasDroppedFiles.closeDialog();
    clearError();
    // Used for legacy backbone studylist
    onClose?.([...pdfFiles, documentUrl].length);
  };

  const handleMakeFilePrimary = useCallback(
    async (fileId: string | number) => {
      try {
        await makePdfFilePrimary(fileId);
      } catch {
        showErrorToast();
      }
    },
    [makePdfFilePrimary, showErrorToast]
  );

  const handleDeletePdfFile = useCallback(
    async (file: TransformedFile) => {
      try {
        await deletePdfFile(file);
      } catch {
        showErrorToast();
      }
    },
    [deletePdfFile, showErrorToast]
  );

  useEffect(() => {
    setOpen(isModalOpen || hasDroppedFiles.isDialogOpenFromDrop);
  }, [isModalOpen, hasDroppedFiles.isDialogOpenFromDrop]);

  useEffect(() => {
    // Upload dropped files when dialog opens
    if (hasDroppedFiles.any && isOpen) {
      hasDroppedFiles.files.forEach((file) => {
        handleUploadFile(file);
      });
      hasDroppedFiles.clearFiles();
    }
  }, [isOpen, hasDroppedFiles, handleUploadFile]);

  useEffect(() => {
    setPdfFiles(referenceFiles);
  }, [referenceFiles]);

  return (
    <Modal isOpen={isOpen} onClose={handleOnDismiss} size="lg">
      <ModalHeader>Upload full text</ModalHeader>
      <ModalBody>
        <Stack spacing={4}>
          <DragAndDropFileUploader
            referenceTitle={reference.title || ''}
            hasError={hasError}
            onFileUpload={handleUploadFile}
          />
          <DocumentUrlUploader
            onUpload={(url) => {
              submitDocumentUrl(url).catch((error?: string) => {
                showErrorToast(error);
              });
            }}
          />

          <div aria-live="polite" className={styles.ListWrapper}>
            {(pdfFiles.length > 0 || documentUrl) && (
              <FileList
                documentUrl={documentUrl || undefined}
                files={pdfFiles}
                makeFilePrimary={handleMakeFilePrimary}
                deleteFile={handleDeletePdfFile}
                unsetDocumentUrl={() => submitDocumentUrl('')}
              />
            )}
          </div>
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Button
          type="brand"
          onClick={handleOnDismiss}
          disabled={uploadingCount > 0}
        >
          Done
        </Button>
      </ModalFooter>
    </Modal>
  );
};
